import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  CButton,
  CCol,
  CImage,
  CForm,
  CFormInput,
  CInputGroup,
  CInputGroupText,
  CRow,
  CAlert,
  CSpinner,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import {
  cilLockLocked,
  cilUser,
  cilEnvelopeClosed,
  cilLowVision,
} from "@coreui/icons";
import axios from "axios";
import swal from "sweetalert";
import bgimage from "../../assets/Images/logo_black.png";
import { BASE_URL } from "../../components/constant_api";

const ApprovUser = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // ── Prefilled fields from invitation link ──────────────
  const [emailAddress, setEmailAddress] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  // ── Editable fields ────────────────────────────────────
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // ── Eye toggle states ──────────────────────────────────
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [userId, setUserId] = useState("");
  const [organisationId, setOrganisationId] = useState("");
  const [roleId, setRoleId] = useState("");
  const [token, setToken] = useState("");

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);

  // =====================================================
  // READ QUERY PARAMS FROM EMAIL LINK
  // =====================================================
  useEffect(() => {
    const uid =
      searchParams.get("UserID") ||
      searchParams.get("userId") ||
      "";

    const orgId =
      searchParams.get("OrganisationID") ||
      searchParams.get("organisationId") || searchParams.get("OrganisationIDs") ||
      "";

    const role =
      searchParams.get("RoleID") 
     
    const tkn = searchParams.get("token") || "";
    const emailParam = searchParams.get("email") || "";

    setUserId(uid);
    setOrganisationId(orgId);
    setRoleId(role);
    setToken(tkn);

    if (emailParam) {
      setEmailAddress(emailParam);
    }

    if (!uid && !tkn) {
      setShowError(true);
      setErrorMessage("Invalid or expired invitation link.");
      setPageLoading(false);
      return;
    }

    fetchInviteDetails(uid, orgId, tkn, role);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =====================================================
  // FETCH INVITE / USER DETAILS
  // =====================================================
  const fetchInviteDetails = async (uid, orgId, tkn, role) => {
    try {
      setPageLoading(true);

      const response = await axios.get(
        `${BASE_URL}/user/invite-details`,
        {
          params: {
            UserID: uid,
            OrganisationID: orgId,
            RoleID: role,
            token: tkn,
          },
        }
      );

      const data = response?.data?.data;

      if (data) {
        setEmailAddress(data.EmailAddress || "");
        setFirstName(data.FirstName || "");
        setLastName(data.LastName || "");
      }
    } catch (error) {
      console.error("Error fetching invite details:", error);

      setShowError(true);

      setErrorMessage(
        error?.response?.data?.message ||
        "Unable to load invitation details. The link may be invalid or expired."
      );
    } finally {
      setPageLoading(false);
    }
  };

  // =====================================================
  // ACCEPT INVITATION
  // =====================================================
  const handleApprove = async () => {
    setShowError(false);

    if (!password || !confirmPassword) {
      setShowError(true);
      setErrorMessage(
        "Please enter and confirm your password."
      );
      return;
    }

    if (password !== confirmPassword) {
      setShowError(true);
      setErrorMessage("Passwords do not match.");
      return;
    }

    if (password.length < 6) {
      setShowError(true);
      setErrorMessage(
        "Password must be at least 6 characters long."
      );
      return;
    }

    try {
      setLoading(true);

      const response = await axios.post(
        `${BASE_URL}/user/approve`,
        {
          UserID: userId,
          OrganisationID: organisationId,
          RoleID: roleId,
          token: token,
          FirstName: firstName,
          LastName: lastName,
          Password: password,
        }
      );

      setShowSuccess(true);

      swal(
        "Invitation Accepted!",
        response?.data?.message ||
        "Invitation accepted successfully.",
        "success"
      ).then(() => {
        navigate("/login");
      });
    } catch (error) {
      console.error("Error accepting invitation:", error);

      setShowError(true);

      setErrorMessage(
        error?.response?.data?.message ||
        "Failed to accept invitation. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-light min-vh-100 d-flex flex-row align-items-center">
      <div className="lgnauth">

        {/* =====================================================
            FORM SECTION
        ====================================================== */}
        <div className="lgnform">
          <CRow className="justify-content-center">
            <CCol md={12}>

              <CForm className="row g-3" noValidate>

                {/* TITLE */}
                <h2 className="lgntxt">
                  Accept Invitation
                </h2>

                {/* ERROR ALERT */}
                {showError && (
                  <CAlert
                    color="warning"
                    dismissible
                    onClose={() => setShowError(false)}
                    className="fade-alert"
                  >
                    {errorMessage}
                  </CAlert>
                )}

                {/* SUCCESS ALERT */}
                {showSuccess && (
                  <CAlert
                    color="success"
                    className="fade-alert"
                  >
                    Invitation accepted successfully.
                  </CAlert>
                )}

                {pageLoading ? (
                  <div className="text-center py-5 page-loader-wrap">
                    <CSpinner
                      color="primary"
                      style={{
                        width: "3rem",
                        height: "3rem",
                      }}
                    />

                    <p className="mt-3 mb-0 text-muted">
                      Loading details...
                    </p>
                  </div>
                ) : (
                  <>

                    {/* =================================================
                        FIRST NAME + LAST NAME - TWO COLUMNS
                    ================================================== */}
                    <CRow className="px-0">

                      {/* FIRST NAME */}
                      <CCol md={6}>
                        <CInputGroup className="mb-2 input-anim">
                          <div className="single-input-field">
                            <CInputGroupText>
                              <CIcon icon={cilUser} />
                            </CInputGroupText>

                            <CFormInput
                              id="FirstName"
                              type="text"
                              placeholder="First Name"
                              value={firstName}
                              onChange={(e) => setFirstName(e.target.value)}
                            />
                          </div>
                        </CInputGroup>
                      </CCol>

                      {/* LAST NAME */}
                      <CCol md={6}>
                        <CInputGroup className="mb-2 input-anim">
                          <div className="single-input-field">
                            <CInputGroupText>
                              <CIcon icon={cilUser} />
                            </CInputGroupText>

                            <CFormInput
                              id="LastName"
                              type="text"
                              placeholder="Last Name"
                              value={lastName}
                              onChange={(e) => setLastName(e.target.value)}
                            />
                          </div>
                        </CInputGroup>
                      </CCol>

                    </CRow>

                    {/* =================================================
                        EMAIL
                    ================================================== */}
                    <CInputGroup className="mb-2 input-anim">
                      <div className="single-input-field">

                        <CInputGroupText>
                          <CIcon icon={cilEnvelopeClosed} />
                        </CInputGroupText>

                        <CFormInput
                          id="EmailAddress"
                          type="email"
                          placeholder="Email Address"
                          value={emailAddress}
                          disabled
                          readOnly
                        />

                      </div>
                    </CInputGroup>

                    {/* =================================================
                        PASSWORD
                    ================================================== */}
                    <CInputGroup className="mb-2 input-anim">
                      <div className="single-input-field position-relative">

                        <CInputGroupText>
                          <CIcon icon={cilLockLocked} />
                        </CInputGroupText>

                        <CFormInput
                          id="Password"
                          type={
                            showPassword
                              ? "text"
                              : "password"
                          }
                          placeholder="Password"
                          value={password}
                          onChange={(e) =>
                            setPassword(e.target.value)
                          }
                          style={{
                            paddingRight: "2.5rem",
                          }}
                        />

                        <span
                          className="eye-toggle"
                          role="button"
                          tabIndex={0}
                          aria-label={
                            showPassword
                              ? "Hide password"
                              : "Show password"
                          }
                          onClick={() =>
                            setShowPassword(
                              (prev) => !prev
                            )
                          }
                          onKeyDown={(e) => {
                            if (
                              e.key === "Enter" ||
                              e.key === " "
                            ) {
                              setShowPassword(
                                (prev) => !prev
                              );
                            }
                          }}
                        >
                          <CIcon
                            icon={cilLowVision}
                            className={
                              showPassword
                                ? "eye-active"
                                : ""
                            }
                          />
                        </span>

                      </div>
                    </CInputGroup>

                    {/* =================================================
                        CONFIRM PASSWORD
                    ================================================== */}
                    <CInputGroup className="mb-3 input-anim">
                      <div className="single-input-field position-relative">

                        <CInputGroupText>
                          <CIcon icon={cilLockLocked} />
                        </CInputGroupText>

                        <CFormInput
                          id="ConfirmPassword"
                          type={
                            showConfirmPassword
                              ? "text"
                              : "password"
                          }
                          placeholder="Confirm Password"
                          value={confirmPassword}
                          onChange={(e) =>
                            setConfirmPassword(
                              e.target.value
                            )
                          }
                          style={{
                            paddingRight: "2.5rem",
                          }}
                        />

                        <span
                          className="eye-toggle"
                          role="button"
                          tabIndex={0}
                          aria-label={
                            showConfirmPassword
                              ? "Hide password"
                              : "Show password"
                          }
                          onClick={() =>
                            setShowConfirmPassword(
                              (prev) => !prev
                            )
                          }
                          onKeyDown={(e) => {
                            if (
                              e.key === "Enter" ||
                              e.key === " "
                            ) {
                              setShowConfirmPassword(
                                (prev) => !prev
                              );
                            }
                          }}
                        >
                          <CIcon
                            icon={cilLowVision}
                            className={
                              showConfirmPassword
                                ? "eye-active"
                                : ""
                            }
                          />
                        </span>

                      </div>
                    </CInputGroup>

                    {/* =================================================
                        ACCEPT INVITATION BUTTON
                    ================================================== */}
                    <CButton
                      color="primary"
                      className="px-4 btnsign d-flex align-items-center justify-content-center gap-2"
                      type="button"
                      disabled={
                        loading || showSuccess
                      }
                      onClick={handleApprove}
                    >
                      {loading && (
                        <CSpinner size="sm" />
                      )}

                      {loading
                        ? "Accepting..."
                        : "Accept Invitation"}
                    </CButton>

                  </>
                )}

              </CForm>

            </CCol>
          </CRow>
        </div>

        {/* =====================================================
            LOGO / WELCOME PANEL
        ====================================================== */}
        <div className="logobg">

          <h2 className="weltxt">
            Welcome To
          </h2>

          <CImage
            src={bgimage}
            height="auto"
            align="center"
            width={300}
            className="mainlgo"
          />

          <p className="qqtxt">
            Lorem Ipsum is simply dummy text of the
            printing and typesetting industry. Lorem
            Ipsum has been the industry's standard dummy
            text ever since the 1500s, when an unknown
            printer took a galley of type and scrambled
            it to make a type specimen book.
          </p>

        </div>

      </div>

      {/* =====================================================
          SCOPED STYLES
      ====================================================== */}
      <style>{`
        .single-input-field {
          position: relative;
          display: flex;
          align-items: center;
          width: 100%;
        }

        .eye-toggle {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #8a93a2;
          transition: color 0.2s ease,
            transform 0.15s ease;
        }

        .eye-toggle:hover {
          color: #321fdb;
        }

        .eye-toggle:active {
          transform: translateY(-50%) scale(0.9);
        }

        .eye-active {
          color: #321fdb;
        }

        .input-anim .form-control {
          transition:
            box-shadow 0.2s ease,
            border-color 0.2s ease;
        }

        .input-anim .form-control:focus {
          box-shadow:
            0 0 0 0.2rem
            rgba(50, 31, 219, 0.15);
        }

        .page-loader-wrap {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          width: 100%;
          animation: fadeIn 0.3s ease-in;
        }

        .fade-alert {
          animation: fadeIn 0.3s ease-in;
        }

        .btnsign {
          transition:
            transform 0.15s ease,
            box-shadow 0.2s ease,
            opacity 0.2s ease;
        }

        .btnsign:not(:disabled):hover {
          transform: translateY(-1px);
          box-shadow:
            0 4px 12px
            rgba(50, 31, 219, 0.25);
        }

        .btnsign:not(:disabled):active {
          transform: translateY(0);
        }

        .btnsign:disabled {
          opacity: 0.75;
          cursor: not-allowed;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-4px);
          }

          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Responsive */
        @media (max-width: 767px) {
          .lgnform .row > .col-md-6 {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default ApprovUser;